import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [videoCategories, setVideoCategories] = useState([]);
  const [recommendedVideos, setRecommendedVideos] = useState([]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/v2/twi/home');
        const { featuredVideo, categories, videoCategories, recommendedVideos } = response.data;
        
        setFeaturedVideo(featuredVideo);
        setCategories(categories);
        setVideoCategories(videoCategories);
        setRecommendedVideos(recommendedVideos);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }
  
  return (
    <div className="videos-home">
      {/* Hero Section */}
      {featuredVideo && (
        <section className="hero-section mb-8">
          <div className="relative rounded-lg overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-black to-transparent z-10"></div>
            <img src={featuredVideo.thumbnail} alt={featuredVideo.title} className="w-full h-[350px] object-cover" />
            <div className="absolute inset-0 z-20 flex items-center">
              <div className="container mx-auto px-6">
                <div className="max-w-lg text-white">
                  <h1 className="text-3xl md:text-4xl font-bold mb-3">{featuredVideo.title}</h1>
                  <p className="text-lg mb-5 opacity-90">{featuredVideo.description}</p>
                  <a href={`/videos/${featuredVideo.slug}`} 
                     className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-full font-medium inline-block">
                    Watch Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* Category Filters */}
      <section className="category-filters mb-8">
        <div className="flex overflow-x-auto py-2 hide-scrollbar space-x-4">
          <a href="/videos" className={`flex-none px-4 py-2 ${!window.location.search.includes('category=') ? 'bg-gray-800 text-white' : 'bg-gray-200'} rounded-full text-sm font-medium`}>
            All
          </a>
          {categories.map(category => (
            <a key={category.id} 
               href={`/videos?category=${category.slug}`}
               className={`flex-none px-4 py-2 ${window.location.search.includes(`category=${category.slug}`) ? 'bg-gray-800 text-white' : 'bg-gray-200'} rounded-full text-sm font-medium`}>
              {category.name}
            </a>
          ))}
        </div>
      </section>
      
      {/* Category Rows */}
      {videoCategories.map(category => (
        <section key={category.id} className="video-category mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">{category.name}</h2>
            <a href={`/videos/results?category=${category.slug}`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              View all
            </a>
          </div>
          
          <div className="overflow-x-auto hide-scrollbar">
            <div className="flex space-x-4 pb-2" style={{ minWidth: 'min-content' }}>
              {category.videos.map(video => (
                <div key={video.id} className="flex-none w-64">
                  <a href={`/videos/${video.slug}`} className="block">
                    <div className="relative mb-2">
                      <img src={video.thumbnail} alt={video.title} className="w-full h-36 object-cover rounded-lg" />
                      <span className="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white text-xs px-1 py-0.5 rounded">
                        {video.duration}
                      </span>
                    </div>
                    <div className="flex">
                      <div className="flex-none mr-3">
                        <div className="w-9 h-9 rounded-full overflow-hidden">
                          <img src={video.user.profile_photo_url} alt={video.user.name} className="w-full h-full object-cover" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium line-clamp-2 mb-1">{video.title}</h3>
                        <p className="text-xs text-gray-600">{video.user.name}</p>
                        <p className="text-xs text-gray-500">
                          {video.views_count.toLocaleString()} views • {video.created_at_diff}
                        </p>
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
      
      {/* Video Grid */}
      <section className="recommended-videos mb-12">
        <h2 className="text-xl font-bold mb-4">Recommended Content</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6">
          {recommendedVideos.map(video => (
            <div key={video.id} className="video-card">
              <a href={`/videos/${video.slug}`} className="block">
                <div className="relative mb-2">
                  <img src={video.thumbnail} alt={video.title} className="w-full h-36 object-cover rounded-lg" />
                  <span className="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white text-xs px-1 py-0.5 rounded">
                    {video.duration}
                  </span>
                </div>
                <div className="flex">
                  <div className="flex-none mr-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden">
                      <img src={video.user.profile_photo_url} alt={video.user.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium line-clamp-2 mb-1">{video.title}</h3>
                    <p className="text-xs text-gray-600">{video.user.name}</p>
                    <p className="text-xs text-gray-500">
                      {video.views_count.toLocaleString()} views • {video.created_at_diff}
                    </p>
                  </div>
                </div>
              </a>
            </div>
          ))}
        </div>
      </section>
      
      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default Home;